import React, {
  FC, HTMLAttributes, useCallback, useState,
} from 'react';
import styled from 'styled-components';

import {
  CoverImage, MiniatureSizesEnum, VideoCoverSource,
} from '~/api/book/bookApiTypes';
import { Genre } from '~/api/book/genreApiTypes';
import { DefaultBookCover } from '~/atomic/atom/DefaultBookCover';
import { getImageUrlBySize, sortByType } from '~/atomic/atom/SimpleCover';
import { isMobileSizeSelector } from '~/feature/media/windowSize.selector';
import { isSupportWebpSelector } from '~/feature/supportWebp/isSupportWebp.selector';
import { lessThan } from '~/lib/mediaQuery';
import { useAppSelector } from '~/store';

import { VideoCover } from './VideoCover';

interface BookPageCoverProps {
  book?: {
    title: string;
    author: string;
    genre: Genre;
  };
  coverImages: CoverImage[];
  videoCoverUrl?: string | null;
  videoCoverSources?: VideoCoverSource[];
}

export const BookPageCover: FC<BookPageCoverProps & HTMLAttributes<HTMLImageElement | HTMLVideoElement>> = ({
  book, coverImages, videoCoverUrl, videoCoverSources, ...props
}) => {
  const { jpeg, webp } = sortByType(coverImages);
  const { isSupport } = useAppSelector(isSupportWebpSelector);
  const isMobileSize = useAppSelector(isMobileSizeSelector);

  // Состояние для отслеживания загрузки видео и управления переходом
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Обработчик загрузки видео
  const handleVideoLoad = useCallback(() => {
    setIsVideoLoaded(true);
  }, []);

  let srcSet: string;

  if (isSupport) {
    srcSet = `${getImageUrlBySize(webp, MiniatureSizesEnum.S)} 100w, `
    + `${getImageUrlBySize(webp, MiniatureSizesEnum.M)} 160w, `
    + `${getImageUrlBySize(webp, MiniatureSizesEnum.L)} 320w`;
  } else {
    srcSet = `${getImageUrlBySize(jpeg, MiniatureSizesEnum.S)} 100w, `
    + `${getImageUrlBySize(jpeg, MiniatureSizesEnum.M)} 160w, `
    + `${getImageUrlBySize(jpeg, MiniatureSizesEnum.L)} 320w`;
  }

  const imageWidth = isMobileSize ? 366 : 270;
  const imageHeight = imageWidth * 1.46;

  const showVideo = !!videoCoverUrl;

  if (!coverImages && !videoCoverUrl) {
    return (
      <SCImageContent>
        <DefaultBookCover
          bookName={book?.title ?? 'Название книги'}
          authorName={book?.author ?? 'Имя Автора'}
          font={{
            min: 4,
            max: 16,
          }}
        />
      </SCImageContent>
    );
  }

  return (
    <SCWrapper>
      {showVideo && (
        <VideoCover
          src={videoCoverUrl}
          sources={videoCoverSources}
          displayWidth={imageWidth}
          width={imageWidth}
          height={imageHeight}
          onCanPlay={handleVideoLoad}
          {...props}
        />
      )}
      <SCBookCoverPicture
        data-is-video-loaded={isVideoLoaded}
      >
        <SCImage
          srcSet={srcSet}
          src={getImageUrlBySize(jpeg, MiniatureSizesEnum.Original)}
          title={`${
            book?.title ?? 'Название книги'
          } / ${
            book?.author ?? 'Автор книги'
          }`}
          alt={`${
            book?.title ?? 'Название книги'
          } - ${
            book?.author ?? 'Автор книги'
          }, ${
            book?.genre?.name ?? 'Жанр книги'
          }`}
          {...props}
          width={imageWidth}
          height={imageHeight}
        />
      </SCBookCoverPicture>
    </SCWrapper>
  );
};

const SCWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const SCBookCoverPicture = styled.div`
  width: 100%;

  &:before {
    content: "";
    position: absolute;
    bottom: -7px;
    left: 50%;
    transform: translateX(-50%);
    height: 18px;
    width: 92%;
    background: #D1D2D2;
    z-index: 1;
    border-radius: 0 0 8px 8px;

    ${lessThan('sm')} {
      bottom: -5px;
      display: none;
    }
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -11px;
    left: 50%;
    transform: translateX(-50%);
    height: 18px;
    width: 86%;
    background: #EFEFEF;
    z-index: 0;
    border-radius: 0 0 8px 8px;

    ${lessThan('sm')} {
      bottom: -9px;
      display: none;
    }
  }

  img {
    position: relative;
    top: 0;
    max-width: 100%;
    max-height: 100%;
    height: auto;
    z-index: 2;
    border-radius: 4px;
  }

  &[data-is-video-loaded='true'] {
    opacity: 0;
  }
`;

export const SCImageContent = styled.div`
  width: 100%;
`;

const SCImage = styled.img`
  background-color: hsla(0,0%,74.5%,.2);
`;
